import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { getDashboardAnalytics } from "../redux/dashboard/dashboardSlice";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import { useLocationFilter } from "../../src/context/LocationContext";

const Card = ({ title, value, icon, color, onClick }) => (
  <div className="col-xl-3 col-sm-6">
    <div
      className="card dashboard-card radius-12 h-100 border-0 shadow-sm"
      style={{
        background: `linear-gradient(135deg, ${color}15, ${color}05)`,
      }}
      onClick={onClick}
    >
      <div className="card-body p-20 d-flex justify-content-between align-items-center">
        <div>
          <p className="text-sm text-secondary mb-6">{title}</p>
          <h3 className="mb-0 fw-bold">{value}</h3>
        </div>

        <div
          className="card-icon w-50-px h-50-px d-flex justify-content-center align-items-center rounded-circle"
          style={{
            background: `${color}20`,
            color: color,
            fontSize: "22px",
          }}
        >
          <Icon icon={icon} />
        </div>
      </div>
    </div>
  </div>
);

const DashBoardLayerNine = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedLocation } = useLocationFilter();

  const { analytics, loading, error } = useSelector(
    (state) => state.dashboard
  );

  useEffect(() => {
    const params = {};

    if (selectedLocation?.value) {
      params.locationId = selectedLocation.value;
    }

    dispatch(getDashboardAnalytics(params));
  }, [dispatch, selectedLocation]);


  if (loading) return <p className="text-center mt-5">Loading dashboard...</p>;
  if (error) return <p className="text-danger text-center">{error}</p>;
  if (!analytics) {
    return <div className="text-center mt-5">Loading charts...</div>;
  }

  const events = analytics?.events || [];
  const users = analytics?.users || [];

  const eventNames = events.map(e => e.name);
  const userCounts = events.map(e => Number(e.userCount) || 0);
  const sessionCounts = events.map(e => Number(e.sessionCount) || 0);

  const roleNames = users.map(u => u.name);
  const roleCounts = users.map(u => Number(u.userCount) || 0);
  const ratingCounts = users.map(u => Number(u.ratingCount) || 0);

  const safeRoleCounts = roleCounts.some(v => v > 0)
    ? roleCounts
    : [1];

  const safeRoleNames = roleCounts.some(v => v > 0)
    ? roleNames
    : ["No Data"];

  return (
    <div className="row gy-4">
      <Card title="Total Users" value={analytics.totalUser} icon="mdi:account-group" color="#4e73df" onClick={() => navigate("/users-list")} />

      <Card title="Logged In Users" value={analytics.totalUserLoggedIn} icon="mdi:account-check" color="#1cc88a" onClick={() => navigate("/active-users")} />

      <Card title="Total Sessions" value={analytics.totalSession} icon="mdi:calendar" color="#f6c23e" onClick={() => navigate("/sessions")} />

      <Card title="Event Locations" value={analytics.eventLocation} icon="mdi:map-marker" color="#36b9cc" onClick={() => navigate("/event-locations")} />

      <Card title="Total Events" value={events.length} icon="mdi:calendar-multiple" color="#6610f2" />

      <Card title="Meeting Requests" value={analytics.totalMeetingRequest} icon="mdi:account-multiple-plus" color="#e74a3b" onClick={() => navigate("/meetings")} />

      <Card title="Chat Requests" value={analytics.totalChatRequest} icon="mdi:chat" color="#5a5c69" onClick={() => navigate("/chat-requests")} />

      <Card title="Feedback" value={analytics.totalFeedback} icon="mdi:message-text" color="#fd7e14" onClick={() => navigate("/feedback-submissions")} />

      <Card title="User Ratings" value={analytics.totalUserRating} icon="mdi:star" color="#20c997" onClick={() => navigate("/ratings")} />

      <Card title="Total Contacts" value={analytics.totalContact} icon="mdi:account-box" color="#0dcaf0" />

      <div className="col-12">
        <div className="card radius-12 shadow-sm border-0">
          <div className="card-header bg-transparent border-bottom">
            <h5 className="fw-semibold mb-0">Top Events Overview</h5>
          </div>

          <div className="card-body">
            <Chart
              options={{
                chart: { type: "bar", toolbar: { show: false } },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    barHeight: "50%",
                  },
                },
                xaxis: {
                  categories: eventNames,
                },
                colors: ["#4e73df", "#1cc88a"],
                legend: {
                  position: "top",
                },
                dataLabels: {
                  enabled: false,
                },
              }}
              series={[
                { name: "Users", data: userCounts },
                { name: "Sessions", data: sessionCounts },
              ]}
              type="bar"
              height={300}
            />
          </div>
        </div>
      </div>

      <div className="col-12">
        <div className="row">

          {/* ================= DONUT ================= */}
          <div className="col-md-6">
            <div className="card radius-12 shadow-sm border-0">
              <div className="card-header bg-transparent border-bottom">
                <h5 className="fw-semibold mb-0">User Role Breakdown</h5>
              </div>

              <div className="card-body">
                <Chart
                  options={{
                    labels: safeRoleNames, // ✅ FIXED
                    colors: [
                      "#4e73df",
                      "#e74a3b",
                      "#f6c23e",
                      "#1cc88a",
                      "#36b9cc",
                      "#fd7e14",
                      "#6f42c1",
                      "#20c997",
                    ],
                    legend: {
                      position: "right",
                    },
                    dataLabels: {
                      enabled: true,
                      formatter: function (val) {
                        return val.toFixed(1) + "%";
                      },
                    },
                    plotOptions: {
                      pie: {
                        donut: {
                          size: "65%",
                          labels: {
                            show: true,
                            total: {
                              show: true,
                              label: "Total",
                              formatter: function () {
                                return roleCounts.reduce((a, b) => a + b, 0);
                              },
                            },
                          },
                        },
                      },
                    },
                  }}
                  series={safeRoleCounts} // ✅ FIXED
                  type="donut"
                  height={300}
                />
              </div>
            </div>
          </div>

          {/* ================= RATINGS ================= */}
          <div className="col-md-6">
            <div className="card radius-12 shadow-sm border-0">
              <div className="card-header bg-transparent border-bottom">
                <h5 className="fw-semibold mb-0">Ratings</h5>
              </div>

              <div className="card-body">

                {ratingCounts.some(v => v > 0) ? (
                  <Chart
                    options={{
                      chart: { type: "bar", toolbar: { show: false } },
                      plotOptions: {
                        bar: {
                          borderRadius: 6,
                          columnWidth: "50%",
                        },
                      },
                      xaxis: {
                        categories: roleNames,
                      },
                      colors: ["#4e73df"],
                      dataLabels: {
                        enabled: false,
                      },
                    }}
                    series={[
                      {
                        name: "Ratings",
                        data: ratingCounts,
                      },
                    ]}
                    type="bar"
                    height={300}
                  />
                ) : (
                  // ✅ FIX: handle no ratings case
                  <div className="text-center py-5 text-muted">
                    <Icon icon="mdi:star-off-outline" width="40" />
                    <p className="mt-2">No Ratings Available</p>
                  </div>
                )}

              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DashBoardLayerNine;